import { codecFamilyOf, type CodecFamily } from './playback.ts';

/**
 * What the *engine* reports about the tracks a manifest actually offers, flattened to R3-plain data
 * so core can reason about it and so the Android port describes its Media3 formats the same way.
 *
 * These are deliberately **snapshots**, not handles. After `player.load()` every Shaka track object
 * is a new instance; R6's recovery re-selects by `key`, so nothing here may be held by reference.
 */

/** DASH `urn:mpeg:dash:role:2011` values this corpus emits. */
export const ROLE_MAIN = 'main';
export const ROLE_ALTERNATE = 'alternate';
export const ROLE_DUB = 'dub';
export const ROLE_DESCRIPTION = 'description';
/**
 * YouTube's "Stable Volume" track — dynamic range compression. It carries `main` *as well*, so a
 * naive "pick the main-role track" lands on it half the time. §7: hide it unless opted in.
 */
export const ROLE_DRC = 'enhanced-audio-intelligibility';

export interface VideoTrackInfo {
  /** `videoTrackKey(height, codec)` — `1080@avc`. Stable across reloads (R6). */
  readonly key: string;
  readonly height: number | null;
  readonly width: number | null;
  /** Verbatim from the manifest — `avc1.640028`, `av01.0.08M.08`. */
  readonly codec: string | null;
  readonly codecFamily: CodecFamily;
  readonly bandwidth: number;
  readonly frameRate: number | null;
  readonly active: boolean;
}

export interface AudioTrackInfo {
  /** `audioTrackKey(language, roles)` — `de#alternate,dub`. Stable across reloads (R6). */
  readonly key: string;
  readonly language: string;
  /** The DASH `<Label>` — "German", "English original (Stable Volume)". */
  readonly label: string | null;
  readonly roles: readonly string[];
  readonly channelsCount: number | null;
  readonly active: boolean;
}

/** A text track as the *engine* sees it, after sideloading. Keyed by `subtitleTrackKey`. */
export interface TextTrackInfo {
  readonly key: string;
  readonly languageCode: string;
  readonly label: string | null;
  readonly active: boolean;
}

/**
 * Caption language codes are **not unique within a video** (§2): `en` appears twice when an
 * auto-generated track exists, and only the label distinguishes them. So the key has to carry the
 * label too, or re-applying a subtitle preference after a reload picks the wrong one half the time.
 */
export function subtitleTrackKey(languageCode: string, label: string): string {
  return `${languageCode.toLowerCase()}#${label.toLowerCase()}`;
}

export function hasRole(roles: readonly string[], role: string): boolean {
  return roles.some((candidate) => candidate.toLowerCase() === role.toLowerCase());
}

export function isDubTrack(track: AudioTrackInfo): boolean {
  return hasRole(track.roles, ROLE_DUB);
}

export function isDrcTrack(track: AudioTrackInfo): boolean {
  return hasRole(track.roles, ROLE_DRC);
}

/** The track the uploader recorded: `main` role, no `dub`. Says nothing about DRC. */
export function isOriginalAudio(track: AudioTrackInfo): boolean {
  return hasRole(track.roles, ROLE_MAIN) && !isDubTrack(track);
}

/** Convenience for adapters building a `VideoTrackInfo` from an engine track. */
export function videoCodecFamily(codec: string | null): CodecFamily {
  return codecFamilyOf(codec ?? '');
}
